import type {
    MarkingEditorState,
    FontSizeRange,
    TextAlignment,
    FontFamily,
    MarkingPosition,
} from '../../types'
import { validateAlignment, validateFontFamily } from './fieldValidation'
import { clampFontSize } from './fontSizeValidation'

/**
 * Type guard для проверки, является ли значение валидным MarkingEditorState
 * @param data - данные для проверки
 * @returns true, если данные являются валидным MarkingEditorState
 */
export function isValidMarkingEditorState(
    data: unknown
): data is MarkingEditorState {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return false
    }

    const state = data as Record<string, unknown>

    // Проверяем обязательные поля
    if (
        typeof state.markMedia !== 'boolean' ||
        typeof state.markMediaColor !== 'string' ||
        (state.markMediaText !== null &&
            typeof state.markMediaText !== 'string') ||
        typeof state.fontSize !== 'number' ||
        typeof state.alignment !== 'string' ||
        typeof state.fontFamily !== 'string' ||
        typeof state.hasBackground !== 'boolean' ||
        (state.backgroundColor !== null &&
            typeof state.backgroundColor !== 'string')
    ) {
        return false
    }

    // Валидация fontSize
    const fontSize = clampFontSize(state.fontSize)
    if (fontSize !== state.fontSize) {
        return false
    }

    // Валидация alignment
    if (!validateAlignment(state.alignment as TextAlignment)) {
        return false
    }

    // Валидация fontFamily
    if (!validateFontFamily(state.fontFamily as FontFamily)) {
        return false
    }

    // Валидация position (если не null)
    if (state.position !== null) {
        if (
            typeof state.position !== 'object' ||
            Array.isArray(state.position) ||
            typeof (state.position as Record<string, unknown>).x !== 'number' ||
            typeof (state.position as Record<string, unknown>).y !== 'number'
        ) {
            return false
        }
    }

    return true
}

/**
 * Type guard для проверки, является ли значение частичным MarkingEditorState
 * @param data - данные для проверки
 * @returns true, если данные являются частичным MarkingEditorState
 */
export function isPartialMarkingEditorState(
    data: unknown
): data is Partial<MarkingEditorState> {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return false
    }

    const state = data as Record<string, unknown>

    // Проверяем только те поля, которые присутствуют
    if (state.markMedia !== undefined && typeof state.markMedia !== 'boolean') {
        return false
    }

    if (
        state.markMediaColor !== undefined &&
        typeof state.markMediaColor !== 'string'
    ) {
        return false
    }

    if (
        state.markMediaText !== undefined &&
        state.markMediaText !== null &&
        typeof state.markMediaText !== 'string'
    ) {
        return false
    }

    if (state.fontSize !== undefined && typeof state.fontSize !== 'number') {
        return false
    }

    if (
        state.alignment !== undefined &&
        !validateAlignment(state.alignment as TextAlignment)
    ) {
        return false
    }

    if (
        state.fontFamily !== undefined &&
        !validateFontFamily(state.fontFamily as FontFamily)
    ) {
        return false
    }

    if (
        state.hasBackground !== undefined &&
        typeof state.hasBackground !== 'boolean'
    ) {
        return false
    }

    if (
        state.backgroundColor !== undefined &&
        state.backgroundColor !== null &&
        typeof state.backgroundColor !== 'string'
    ) {
        return false
    }

    if (state.position !== undefined && state.position !== null) {
        if (
            typeof state.position !== 'object' ||
            Array.isArray(state.position) ||
            typeof (state.position as Record<string, unknown>).x !== 'number' ||
            typeof (state.position as Record<string, unknown>).y !== 'number'
        ) {
            return false
        }
    }

    return true
}

/**
 * Округление числа до определенного количества знаков после запятой
 * Используется для сравнения позиций, чтобы избежать проблем с числами с плавающей точкой
 */
function roundToDecimal(value: number, decimals = 2): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

/**
 * Глубокое сравнение двух объектов состояния редактора
 */
export function hasStateChanges(
    current: MarkingEditorState,
    initial: MarkingEditorState
): boolean {
    // Сравнение position с учетом null и округлением для избежания проблем с числами с плавающей точкой
    const positionChanged =
        (current.position === null) !== (initial.position === null) ||
        (current.position !== null &&
            initial.position !== null &&
            (roundToDecimal(current.position.x) !==
                roundToDecimal(initial.position.x) ||
                roundToDecimal(current.position.y) !==
                    roundToDecimal(initial.position.y)))

    return (
        current.markMedia !== initial.markMedia ||
        current.markMediaColor !== initial.markMediaColor ||
        current.markMediaText !== initial.markMediaText ||
        current.fontSize !== initial.fontSize ||
        current.alignment !== initial.alignment ||
        current.fontFamily !== initial.fontFamily ||
        current.hasBackground !== initial.hasBackground ||
        current.backgroundColor !== initial.backgroundColor ||
        positionChanged
    )
}
